import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { RevxMeasure } from 'components/RevxMeasure';
import { MeasureType } from 'domains/measurementDomain';
import { Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import {
  Parcel,
  ParcelCategoryDisplay,
  ParcelCategoryEnum,
} from 'domains/parcelDomain';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ParcelBaseTable = () => {
  const { columnTitle } = useMeasurementUnit();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.parcel.getBaseParcels
  );

  const pagedData = useAppSelector(
    (state) => state.parcel.baseSelectState.pagedData
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const baseColumns: TableColumnsType<Parcel> = [
    {
      title: t('common.revxId'),
        width: 60,
      dataIndex: 'revxId',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.parcel.columnTitle.category'),
      dataIndex: 'category',
      render: (value: ParcelCategoryEnum) => {
        return ParcelCategoryDisplay[value];
      },
    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.width'),
        MeasureType.LENGTH
      ),
      dataIndex: 'widthIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.widthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.height'),
        MeasureType.LENGTH
      ),
      dataIndex: 'heightIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.heightMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.length'),
        MeasureType.LENGTH
      ),
      dataIndex: 'lengthIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.lengthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.weight'),
        MeasureType.WEIGHT
      ),
      dataIndex: 'weightLb',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.weightKg}
            type={MeasureType.WEIGHT}
            withSymbol={false}
          />
      ),    },
    {
      title: t('platform.parcel.columnTitle.price'),
      dataIndex: 'price',
      valueType: 'money',
    },
  ];

  const actionColumns: TableColumnsType<Parcel> = [
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="select"
            size="small"
            onClick={() => {
              dispatch.parcel.updateBaseSelectItem(record);
              dispatch.parcel.updateBaseSelectVisible(false);
            }}
          >
            {t('common.select')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
          }

          if (pagination.current !== currentPage) {
            setCurrentPage(pagination.current);
          }

          break;
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    dispatch.parcel.getBaseParcels({
      page: {
        currentPage,
        pageSize,
      },
      query: {
        active: true,
      },
    });
  }, [currentPage, pageSize, dispatch.parcel]);

  return (
    <RevxTable<Parcel>
      loading={loading}
      size="small"
      columns={[...baseColumns, ...actionColumns]}
      pagination={{
        pageSize: pageSize,
        total: pagedData?.totalElements,
        current: currentPage,
        showTotal: (total) => t('common.pagerTotal', { total: total }),
      }}
      indentSize={20}
      width={'auto'}
      minWidth={0}
      scroll={{
        y: 'calc(100vh - 220px)',
      }}
      dataSource={pagedData?.content || []}
      onChange={onTableChange}
    />
  );
};
